import { runIO } from '~/utils/monadUtil';
import { isEmpty } from 'lodash';
import { always, converge, curry, pipe, prop, subtract } from 'ramda';
import { IO } from 'ramda-fantasy';

// select :: String -> IO Node
export const select = (selector) => new IO(() => document.querySelector(selector));

// selectAllFrom :: String -> Node -> Node
export const selectAllFrom = curry((selector, fromNode) => new IO(() => fromNode.querySelectorAll(selector)));

// setStyle :: Node -> String -> String -> Node
export const setStyle = curry((key, value, node) => {
  node.style[key] = value;
  return node;
});

// getStyle :: Node -> String -> String
export const getStyle = curry((key, node) => node.style[key]);

export const getMiddleOfHeight = (node) => {
  return node.clientHeight / 2;
};

export const isScrollTop = (scrollTop: number) => {
  return scrollTop === 0;
};

export const isScrollBottom = ({ clientHeight, scrollHeight, scrollTop }) => {
  return scrollHeight > clientHeight && clientHeight + scrollTop + 10 >= scrollHeight;
};

// scrollTop :: Node -> Number -> Node
export const scrollTo = curry((node, scrollTop) => {
  node.scrollTop = scrollTop;
  return node;
});

export const hasScrollbar = ({ scrollHeight, clientHeight }) => {
  return scrollHeight > clientHeight;
};

type VoidReturn = () => void;
export type SingletonRequestAnimationFrame<T extends [...args: any]> = (
  callback: (...args: T) => void
) => ((...args: T) => VoidReturn) & { dispose: VoidReturn };

const singletonRequestAnimationFrame = <T extends [...args: any]>(
  callback: (...args: T) => void
): ReturnType<SingletonRequestAnimationFrame<T>> => {
  let requestAnimationFrameId = -1;
  const dispose = () => requestAnimationFrameId !== -1 && window.cancelAnimationFrame(requestAnimationFrameId);
  const cb = (...args: T): (() => void) => {
    dispose();
    requestAnimationFrameId = window.requestAnimationFrame(() => {
      requestAnimationFrameId = -1;
      callback(...args);
    });
    return dispose;
  };
  cb.dispose = dispose;
  return cb;
};

// getRemainScrollHeight :: DOM -> IO number
export const getRemainScrollHeight = (node) => IO.of(node).map(() => node.scrollHeight - node.scrollTop);

const getContainerScrollPositionByAlign = (align, container) => {
  if (align === 'middle') {
    return getMiddleOfHeight(container);
  }

  return 0;
};

const getNodeScrollPosition = curry((align, fromNode, node) => {
  return converge(subtract, [prop('offsetTop'), always(getContainerScrollPositionByAlign(align, fromNode))])(node);
});

// scrollToNode :: Node -> Node -> Node
export const scrollToNode = curry((align, scrollContainer, node) => {
  requestAnimationFrame(() => {
    pipe<HTMLElement, any, any>(getNodeScrollPosition(align, scrollContainer), scrollTo(scrollContainer))(node);
  });
  return node;
});

export const isScrollable = (node) => {
  const isElement = node instanceof HTMLElement;
  const overflowY = isElement && window.getComputedStyle(node).overflowY;
  return overflowY !== 'visible' && overflowY !== 'hidden';
};

export const findScrollParent = (node) => {
  if (isEmpty(node)) {
    return null;
  }

  if (isScrollable(node) && node.scrollHeight >= node.clientHeight) {
    return node;
  }

  return findScrollParent(node.parentNode) || document.body;
};

export const hasClass = curry((className, node) => {
  return node.classList.contains(className);
});

export const addClass = curry((className, node) => {
  node.classList.add(className);
  return node;
});

export const scrollIntoViewIfNeeded = (node) => {
  return node.scrollIntoViewIfNeeded(false);
};

export const removeClass = curry((className, node) => {
  node.classList.remove(className);
  return node;
});

const isTextSelectableNode = (node) => {
  return node.nodeName === 'INPUT' || node.nodeName === 'TEXTAREA';
};

const copyTextSelectableNode = (node) => {
  node.select();
  document.execCommand('copy');
};

const copyNonTextSelectableNode = (node) => {
  if (window.getSelection) {
    const selection = window.getSelection();
    const range = document.createRange();
    range.selectNodeContents(node);
    selection.removeAllRanges();
    selection.addRange(range);
    document.execCommand('Copy');
  }
};

export const copyText = (selector) => {
  pipe(select, runIO(copyNodeText))(selector);
};

export const copyNodeText = (node) => {
  if (isTextSelectableNode(node)) {
    copyTextSelectableNode(node);
  } else {
    copyNonTextSelectableNode(node);
  }
};

const getScrollParent = (node) => {
  if (node === document || node === null) {
    return document.body;
  }

  if (node.scrollHeight > node.clientHeight) {
    return node;
  } else {
    return getScrollParent(node.parentNode);
  }
};

const isHidden = (node) => {
  return (
    !document.body.contains(node) ||
    node.offsetWidth === 0 ||
    node.offsetHeight === 0 ||
    window.getComputedStyle(node).visibility === 'hidden'
  );
};

const containsEq = (maybeChild: HTMLElement | undefined, maybeContainer: HTMLElement) => {
  return IO(() => {
    return maybeContainer === maybeChild || maybeContainer.contains(maybeChild);
  });
};

const isClickableDom = (dom: HTMLElement, isClick: (dom: HTMLElement) => boolean): boolean => {
  while (dom) {
    if (isClick(dom)) {
      return true;
    }
    dom = dom.parentElement;
  }
  return false;
};

function isInViewport(element) {
  const rect = element.getBoundingClientRect();
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
}

function isInDom(event: MouseEvent, wrapper$: HTMLElement, padding = 0) {
  if (!wrapper$) {
    return;
  }
  const { clientX, clientY } = event;
  const { left, right, top, bottom } = wrapper$.getBoundingClientRect();
  return (
    left - padding <= clientX && clientX <= right + padding && top - padding <= clientY && clientY <= bottom + padding
  );
}

const domUtil = {
  addClass,
  removeClass,
  setStyle,
  getStyle,
  getScrollParent,
  isScrollBottom,
  isScrollTop,
  isHidden,
  hasScrollbar,
  select,
  containsEq,
  singletonRequestAnimationFrame,
  isClickableDom,
  isInViewport,
  isInDom,
};
export default domUtil;
